import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import jwtDecode from 'jwt-decode'
import { AxiosError } from 'axios'
import { binariiApi } from '../../../../utils/axiosConfig'
import LoginPage from '../../../pages/LoginPage'
import VerifyPage from '../../../pages/VerifyPage'
import ForgotPassword from '../../../pages/ForgotPassword'
import { EUserRole, userJWTData } from '../../../../redux/user/userTypes'

/**
 * A wizard component for guiding users through the Multi-Factor Authentication (MFA) setup process.
 * Manages the steps of login and verification for MFA.
 *
 * @component
 * @returns {JSX.Element} - The rendered MultiFactorAuthWizzard component.
 */
const MultiFactorAuthWizzard: React.FC = () => {
  const navigate = useNavigate()

  const [currentStep, setCurrentStep] = useState<number>(1)
  const [jwtToken, setJwtToken] = useState<string>('')
  const [errorMessageLogin, setErrorMessageLogin] = useState<string>('')
  const [userEmail, setUserEmail] = useState<string>('')

  const [hasExistingSession] = useState<boolean>(() => !!localStorage.getItem('token'))

  /**
   * Redirect user if already logged in
   */
  useEffect(() => {
    if (hasExistingSession) {
      navigate('/', { replace: true })
    }
  }, [hasExistingSession, navigate])

  /**
   * Redirects user to a specific route or refreshes the page.
   */
  const loginRedirect = (navigateTo?: string) => {
    if (navigateTo) {
      navigate(navigateTo)
      return
    }
    navigate(0)
  }

  /**
   * Sends MFA verification code to user
   */
  const sendCodeToUser = (token?: string) => {
    binariiApi.post(
      'code/user/send',
      {},
      {
        headers: { Authorization: `Bearer ${token || jwtToken}` },
      }
    )
  }

  /**
   * Handles the submission of the login form, initiates MFA setup if needed.
   *
   * @async
   * @function
   * @param {string} username - The user's username.
   * @param {string} password - The user's password.
   * @returns {Promise<void>} - A Promise that resolves when the login and MFA setup are complete.
   */
  const onLoginFormSubmit = async (username: string, password: string): Promise<void> => {
    try {
      // Clear any previous error messages
      setErrorMessageLogin('')

      // Attempt to perform a login request to the backend API
      const loginResponse = await binariiApi.post('login', { username, password })
      // Check if a token was received
      if (!loginResponse?.data?.token) {
        return
      }

      const { token } = loginResponse.data
      setJwtToken(token)

      const { mfa, roles, email } = jwtDecode(token) as userJWTData
      setUserEmail(email)

      // If MFA is not required
      if (!mfa) {
        localStorage.setItem('token', token)

        if (roles.includes(EUserRole.ROLE_SUPER_ADMIN)) {
          loginRedirect('/super-admin/dashboard')
          return
        }

        loginRedirect()
        return
      }
      // If MFA is required
      sendCodeToUser(token)
      setCurrentStep(2)
    } catch (err: any) {
      const error = err as AxiosError<{ error: string }>
      setErrorMessageLogin(
        error.response?.data?.error || 'An unexpected error occurred during login.'
      )
      throw err
    }
  }

  /**
   * Handles the submission of the verification form.
   *
   * @async
   * @function
   * @param {string} enteredCode - The verification code entered by the user.
   * @returns {Promise<void>} - A Promise that resolves when the verification is complete.
   */
  const onVerifyPageSubmit = async (enteredCode: string): Promise<void> => {
    try {
      await binariiApi.post(
        'code/user/verify',
        { enteredCode },
        {
          headers: { Authorization: `Bearer ${jwtToken}` },
        }
      )

      localStorage.setItem('token', jwtToken)
      loginRedirect()
    } catch {
      // intentionally silent (handled inside VerifyPage)
    }
  }

  /**
   * Function to navigate to the "Forgot Password" step.
   * Sets the current step to 3, indicating the transition to the "Forgot Password" step.
   */
  const navigateToForgotPassword = () => {
    setErrorMessageLogin('')
    setCurrentStep(3)
  }
  /**
   * Function to navigate to the "Login" step.
   * Sets the current step to 1, indicating the transition to the "Login" step.
   */
  const navigateToLogin = () => {
    setErrorMessageLogin('')
    setCurrentStep(1)
  }

  /**
   * Dynamically compute the current page based on step.
   * This ensures updated state values (like errorMessageLogin) propagate correctly.
   */

  /**
   * 🔐 Render guards AFTER hooks
   */
  if (hasExistingSession) {
    return null
  }

  /**
   * Step rendering
   */
  switch (currentStep) {
    case 1:
      return (
        <LoginPage
          loginSubmitFunction={onLoginFormSubmit}
          navigateToForgotPassword={navigateToForgotPassword}
          errorMessageLogin={errorMessageLogin}
        />
      )

    case 2:
      return (
        <VerifyPage
          resendCode={sendCodeToUser}
          onVerifyFormSubmit={onVerifyPageSubmit}
          email={userEmail}
        />
      )

    case 3:
      return <ForgotPassword navigateToLogin={navigateToLogin} />

    default:
      return <>Invalid step</>
  }
}

export default MultiFactorAuthWizzard
